import './Editor.scss';
import AssetSelectModal from '../contentful/AssetSelectModal';
import asset from '../../model/asset';
import {AutoAffix} from 'react-overlays';
import WidgetBlock from './WidgetBlock';
import ImageBlock from './ImageBlock';
import ImageBlockUrlModal from './ImageBlockUrlModal';
import {
  List as immutableList,
  Repeat as immutableRepeat
} from 'immutable';
import React from 'react';
import {
  AtomicBlockUtils,
  CharacterMetadata,
  ContentBlock,
  ContentState,
  CompositeDecorator,
  Editor as DraftEditor,
  EditorState,
  genKey,
  RichUtils,
  Modifier,
  SelectionState
} from 'draft-js';
import {
  stateToHTML
} from 'draft-js-export-html';
import {
  stateFromHTML
} from 'draft-js-import-html';
import TurndownService from 'turndown';
import MarkdownIt from 'markdown-it';

// MGD-172
// Remove the escape rule for * as it causes a lot of trouble for MG
const turnDownEscapes = [
  [/\\/g, '\\\\'],
  // [/\*/g, '\\*'],
  [/^-/g, '\\-'],
  [/^\+ /g, '\\+ '],
  [/^(=+)/g, '\\$1'],
  [/^(#{1,6}) /g, '\\$1 '],
  [/`/g, '\\`'],
  [/^~~~/g, '\\~~~'],
  [/\[/g, '\\['],
  [/\]/g, '\\]'],
  [/^>/g, '\\>'],
  // [/_/g, '\\_'],
  [/^(\d+)\. /g, '$1\\. ']
];

TurndownService.prototype.escape = function (string) {
  return turnDownEscapes.reduce(function (accumulator, escape) {
    return accumulator.replace(escape[0], escape[1]);
  }, string);
};

const EntityTypes = {
  IMAGE: 'IMAGE',
  WIDGET: 'WIDGET'
};

const blockRenderer = (block, cb, urlEditor) => {
  let blockType = block.getType();

  if (blockType !== 'atomic' && blockType !== 'atomicImage' && blockType !== 'atomicWidget') {
    return;
  }

  if (blockType === 'atomic') {
    // We filter on the text value as we have no other way to differentiate atomic blocks on!?
    blockType = block.getText('text');
  }

  if (blockType === 'atomicImage') {
    return {
      component: ImageBlock,
      editable: true,
      props: {
        urlEditor: urlEditor,
        changeHandler: cb,
        url: block.getData().get('url'),
        urlInTab: block.getData().get('urlInTab')
      }
    };
  } else if (blockType === 'atomicWidget') {
    return {
      component: WidgetBlock,
      editable: false,
      props: {
        widget: block.getData().get('widget'),
        data: block.getData().get('data')
      }
    };
  }
};

const insertImageBlock = (editorState, image) => {
  let content = editorState.getCurrentContent();

  content = content.createEntity(
    EntityTypes.IMAGE,
    'IMMUTABLE',
    {
      image: image
    }
  );
  const entityKey = content.getLastCreatedEntityKey();

  editorState = EditorState.push(
    editorState,
    content,
    'apply-entity'
  );

  return AtomicBlockUtils.insertAtomicBlock(
    editorState,
    entityKey,
    'atomicImage'
  );
};

const blocksInternalToDraft = (internal) => {
  let content = ContentState.createFromBlockArray([]);
  const md = new MarkdownIt();

  internal.forEach((block) => {
    if (block.type === 'imageBlock') {
      content = content.createEntity(
        EntityTypes.IMAGE,
        'IMMUTABLE',
        {
          image: block.image,
          url: block.url,
          urlInTab: block.urlInTab
        }
      );

      const entityKey = content.getLastCreatedEntityKey();
      const charData = CharacterMetadata.create({entity: entityKey});
      const contentBlock = new ContentBlock({
        key: genKey(),
        // We use type "atomic" as it is some kind of magic thing in draft.js which handles e.g. backspack correctly.
        // We then use text to differentiate between the atomic types :s
        type: 'atomic',
        text: 'atomicImage',
        characterList: immutableList(immutableRepeat(charData, 1))
      });

      content = content.set(
        'blockMap',
        content.getBlockMap().set(
          contentBlock.getKey(),
          contentBlock
        )
      );
    } else if (block.type === 'textBlock') {
      const html = md.render(block.text);

      const blockState = stateFromHTML(html);

      content = content.set(
        'blockMap',
        content.getBlockMap().merge(
          blockState.blockMap
        )
      );
    } else if (block.type === 'widgetBlock') {
      content = content.createEntity(
        EntityTypes.WIDGET,
        'IMMUTABLE',
        {
          widget: block.widget,
          data: block.data
        }
      );

      const entityKey = content.getLastCreatedEntityKey();
      const charData = CharacterMetadata.create({entity: entityKey});
      const contentBlock = new ContentBlock({
        key: genKey(),
        // We use type "atomic" as it is some kind of magic thing in draft.js which handles e.g. backspack correctly.
        // We then use text to differentiate between the atomic types :s
        type: 'atomic',
        text: 'atomicWidget',
        characterList: immutableList(immutableRepeat(charData, 1))
      });

      content = content.set(
        'blockMap',
        content.getBlockMap().set(
          contentBlock.getKey(),
          contentBlock
        )
      );
    }
  });

  function findLinkEntities(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();

        return (
          entityKey !== null &&
          contentState.getEntity(entityKey).getType() === 'LINK'
        );
      },
      callback
    );
  }


  const Link = (props) => {
    const {url, title, target} = props.contentState.getEntity(props.entityKey).getData();

    return (
      <a href={url} title={`${title}: ${url}`} target={target}>
        {props.children}
      </a>
    );
  };

  const decorator = new CompositeDecorator([{
    strategy: findLinkEntities,
    component: Link
  }]);

  return EditorState.createWithContent(content, decorator);
};

const blocksDraftToTextBlock = (blocks) => {
  const content = ContentState.createFromBlockArray(blocks);
  const html = stateToHTML(content);

  const turndownService = new TurndownService({
    strongDelimiter: '__'
  });

  // Keep all unknown html tags
  turndownService.keep(function () {
    return true;
  });

  // Keep links as html links (to preserve target attributes)
  turndownService.addRule('link', {
    filter: ['a'],
    replacement: function (content, node) {
      return node.outerHTML;
    }
  });


  const markdown = turndownService.turndown(html);

  return {
    type: 'textBlock',
    text: markdown
  };
};

const createImageBlock = (data) => {
  return {
    type: 'imageBlock',
    image: data.image,
    url: data.url,
    urlInTab: data.urlInTab
  };
};

const createWidgetBlock = (data) => {
  return {
    type: 'widgetBlock',
    widget: data.widget,
    data: data.data
  };
};

const blocksDraftToInternal = (contentState) => {
  const blocks = [];
  let currentHtmlBlocks = [];

  contentState.getBlocksAsArray().forEach((block) => {
    let blockType = block.getType();

    if (blockType === 'atomic') {
      // We filter on the text value as we have no other way to differentiate atomic blocks on!?
      blockType = block.getText('text');
    }

    if (blockType === 'atomicImage') {
      let data = block.getData().toJS();

      // If no customData has been set directly on the block, fetch data from contentstate
      if (Object.keys(data).length === 0) {
        const targetBlock = block.getEntityAt(0);
        if (targetBlock) {
          data = contentState.getEntity(targetBlock).getData();
        }
      }

      if (data) {
        // First "commit" the current plain HTML:
        if (currentHtmlBlocks.length) {
          const textBlock = blocksDraftToTextBlock(currentHtmlBlocks);

          blocks.push(textBlock);
          currentHtmlBlocks = [];
        }

        // Then add the image block:
        blocks.push(createImageBlock(data));
      }
    } else if (blockType === 'atomicWidget') {
      // Then add the widget block:
      let data = block.getData().toJS();

      // If no customData has been set directly on the block, fetch data from contentstate
      if (Object.keys(data).length === 0) {
        const targetBlock = block.getEntityAt(0);
        if (targetBlock) {
          data = contentState.getEntity(targetBlock).getData();
        }
      }

      if (data) {
      // First "commit" the current plain HTML:
        if (currentHtmlBlocks.length) {
          const textBlock = blocksDraftToTextBlock(currentHtmlBlocks);

          blocks.push(textBlock);
          currentHtmlBlocks = [];
        }

        // Then add the widget block:
        blocks.push(createWidgetBlock(data));
      }
    } else {
      currentHtmlBlocks.push(block);
    }
  });

  // "Commit" any remaining HTML:
  if (currentHtmlBlocks.length) {
    const textBlock = blocksDraftToTextBlock(currentHtmlBlocks);
    blocks.push(textBlock);
  }

  return blocks;
};

class Editor extends React.Component {
  constructor(props) {
    super(props);

    let editorState = null;

    if (this.props.blocks.length) {
      editorState = blocksInternalToDraft(
        this.props.blocks
      );
    } else {
      editorState = EditorState.createEmpty();
    }

    this.state = {
      editorState: editorState,
      selecting: false,
      imageUrlEditor: false,
      urlEditor: false,
      disabled: false,
      imageInfo: {},
      urlInfo: {}
    };

    this.handleSelectionToggle = this.handleSelectionToggle.bind(this);
    this.handleEditorChange = this.handleEditorChange.bind(this);
    this.handleContentStateDataChange = this.handleContentStateDataChange.bind(this);
    this.handleKeyCommand = this.handleKeyCommand.bind(this);
    this.handleSelection = this.handleSelection.bind(this);
    this.handleSetBlockType = this.handleSetBlockType.bind(this);
    this.handleToggleInlineStyle = this.handleToggleInlineStyle.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.handleImageUrlToggle = this.handleImageUrlToggle.bind(this);
    this.handleUrlToggle = this.handleUrlToggle.bind(this);
    this.setImageUrl = this.setImageUrl.bind(this);
    this.handleLeaseWidget = this.handleLeaseWidget.bind(this);
    this.setUrl = this.setUrl.bind(this);
  }

  /*
  componentDidUpdate(prevProps) {
    // Update the internal editorState to match the blocks set in the
    // properties:
    const blocks = blocksInternalToDraft(this.props.blocks);
    const contentState = ContentState.createFromBlockArray(blocks);
    const newState = EditorState.createWithContent(contentState);
  }
  */

  handleEditorChange(newState, resetFocus) {
    const blocks = blocksDraftToInternal(newState.getCurrentContent());

    if (!this.props.disabled) {
      this.props.onChange(blocks);

      this.setState({
        editorState: newState
      }, () => {
        if (resetFocus) {
          setTimeout(() => this.refEditor.focus(), 0);
        }
      });
    }
  }

  handleContentStateDataChange(contentState) {
    const newState = EditorState.push(
      this.state.editorState,
      contentState,
      'change-block-data'
    );

    return this.handleEditorChange(newState);
  }

  handleSelectionToggle(e) {
    if (e) {
      e.preventDefault();
    }

    this.setState({
      selecting: !this.state.selecting
    });
  }

  handleFileUpload(file) {
    console.log(file);

    this.setState({
      selecting: false
    });

    return asset.upload({
      filename: file.name,
      contentType: file.type,
      content: file
    }).then((asset) => {
      this.handleSelection(asset);

      this.setState({
        disabled: false
      });
    });
  }

  handleImageUrlToggle(imageBlockInfo) {
    this.imageBlockInfo = imageBlockInfo;

    this.setState({
      imageUrlEditor: !this.state.imageUrlEditor,
      imageInfo: imageBlockInfo ? {
        url: imageBlockInfo.url,
        urlInTab: imageBlockInfo.urlInTab
      } : {}
    });
  }

  handleUrlToggle(e) {
    e.preventDefault();

    const {editorState} = this.state;
    const contentState = editorState.getCurrentContent();
    const startKey = editorState.getSelection().getStartKey();
    const startOffset = editorState.getSelection().getStartOffset();
    const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
    const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);

    let linkData;

    if (linkKey) {
      const linkInstance = contentState.getEntity(linkKey);
      linkData = linkInstance.getData();
    }

    this.setState({
      urlEditor: !this.state.urlEditor,
      urlInfo: linkData ? {
        url: linkData.url,
        urlInTab: linkData.target
      } : {}
    });
  }

  setImageUrl(url, urlInTab) {
    const {block, contentState, data} = this.imageBlockInfo;
    const sel = SelectionState.createEmpty(block.getKey());

    const newState = Modifier.setBlockData(
      contentState,
      sel,
      {
        image: data.image,
        urlInTab: urlInTab,
        url: url
      }
    );

    this.handleContentStateDataChange(newState);
  }

  setUrl(url, urlInTab) {
    const {editorState} = this.state;

    if (!url) {
      this.handleEditorChange(RichUtils.toggleLink(
        editorState,
        editorState.getSelection(),
        null
      ), true);

      return;
    }

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'LINK',
      'MUTABLE',
      {
        url: url,
        target: urlInTab ? '_blank' : undefined
      }
    );

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    // let newEditorState = EditorState.set(editorState, {currentContent: contentStateWithEntity});
    const newEditorState = RichUtils.toggleLink(
      editorState,
      editorState.getSelection(),
      entityKey
    );

    this.handleEditorChange(newEditorState, true);
  }

  handleSetBlockType(e, type) {
    e.preventDefault();

    const newState = RichUtils.toggleBlockType(this.state.editorState, type);

    this.handleEditorChange(newState, true);
  }

  handleLeaseWidget(e) {
    e.preventDefault();

    let editorState = this.state.editorState;

    let content = editorState.getCurrentContent();

    content = content.createEntity(
      EntityTypes.WIDGET,
      'IMMUTABLE',
      {
        widget: 'leaseBasic'
      }
    );
    const entityKey = content.getLastCreatedEntityKey();

    editorState = EditorState.push(
      editorState,
      content,
      'apply-entity'
    );

    const newState = AtomicBlockUtils.insertAtomicBlock(
      editorState,
      entityKey,
      'atomicWidget'
    );

    this.handleEditorChange(newState, true);
  }

  handleSelection(asset) {
    const newState = insertImageBlock(this.state.editorState, asset);

    this.setState({
      selecting: false
    });

    this.handleEditorChange(newState, true);
  }

  handleToggleInlineStyle(e, style) {
    e.preventDefault();

    const newState = RichUtils.toggleInlineStyle(this.state.editorState, style);

    this.handleEditorChange(newState, true);
  }

  handleKeyCommand(command) {
    const newState = RichUtils.handleKeyCommand(this.state.editorState, command);
    if (newState) {
      this.handleEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  }

  render() {
    const {editorState} = this.state;
    const currentBlockType = RichUtils.getCurrentBlockType(editorState);
    const currentLink = RichUtils.currentBlockContainsLink(editorState);
    const currentStyle = editorState.getCurrentInlineStyle();
    const isSelection = !editorState.getSelection().isCollapsed();
    const disabled = this.props.disabled || this.state.disabled;

    return (
      <div className={`${disabled ? 'disabled ' : ''}content-editor`}>
        <AutoAffix affixClassName="affixed">
          <div className="content-editor-actions">
            <div className="content-editor-actions-block">
              <button
                className={`${currentBlockType === 'header-one' ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleSetBlockType(e, 'header-one')}
                disabled={disabled}
              >
                H1
              </button>
              &nbsp;
              <button
                className={`${currentBlockType === 'header-two' ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleSetBlockType(e, 'header-two')}
                disabled={disabled}
              >
                H2
              </button>
              &nbsp;
              <button
                className={`${currentBlockType === 'header-three' ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleSetBlockType(e, 'header-three')}
                disabled={disabled}
              >
                H3
              </button>
              &nbsp;
              {/*
              <button
                className={(currentBlockType === 'header-four' ? 'active ' : '') + 'btn btn-link'}
                onClick={e => {
                  this.handleSetBlockType(e, 'header-four');
                }}
                disabled={disabled}
              >
                H4
              </button>
              &nbsp;
              <button
                className={(currentBlockType === 'header-five' ? 'active ' : '') + 'btn btn-link'}
                onClick={e => this.handleSetBlockType(e, 'header-five')}
                disabled={disabled}
              >
                H5
              </button>
              &nbsp;
              <button
                className={(currentBlockType === 'header-six' ? 'active ' : '') + 'btn btn-link'}
                onClick={e => this.handleSetBlockType(e, 'header-six')}
                disabled={disabled}
              >
                H6
              </button>
              &nbsp;
              */}
              <button
                className={`${currentBlockType === 'unordered-list-item' ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleSetBlockType(e, 'unordered-list-item')}
                disabled={disabled}
              >
                UL
              </button>
              &nbsp;
              <button
                className={`${currentBlockType === 'ordered-list-item' ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleSetBlockType(e, 'ordered-list-item')}
                disabled={disabled}
              >
                OL
              </button>
              &nbsp;
              <button
                className={`${currentBlockType === 'blockquote' ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleSetBlockType(e, 'blockquote')}
                disabled={disabled}
              >
                Sitat
              </button>
              &nbsp;
              <button
                className={`${currentBlockType === 'unstyled' ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleSetBlockType(e, 'unstyled')}
                disabled={disabled}
              >
                P
              </button>
              &nbsp;
              <button
                className={`${currentStyle.has('BOLD') ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleToggleInlineStyle(e, 'BOLD')}
                disabled={disabled}
              >
                <strong>F</strong>
              </button>
              &nbsp;
              <button
                className={`${currentStyle.has('ITALIC') ? 'active ' : ''}btn btn-link`}
                onClick={(e) => this.handleToggleInlineStyle(e, 'ITALIC')}
                disabled={disabled}
              >
                <em>K</em>
              </button>
            </div>
            {/*
            <button
              className={`${currentStyle.has('UNDERLINE') ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleToggleInlineStyle(e, 'UNDERLINE')}
              disabled={disabled}
            >
              <u>U</u>
            </button>
            &nbsp;
            */}
            <div className="content-editor-actions-insert">
              <button
                className="btn btn-link"
                onClick={this.handleSelectionToggle}
                disabled={disabled}
              >
                +&nbsp;Bilde
              </button>
              {this.props.enableLeaseWidget && <button
                className="btn btn-link"
                onClick={this.handleLeaseWidget}
                disabled={disabled}
              >
                +&nbsp;Lease widget
              </button>}
              &nbsp;
              <button
                className={`${currentLink ? 'active ' : ''}btn btn-link`}
                onClick={this.handleUrlToggle}
                disabled={disabled || !isSelection}
              >
                Link
              </button>
              &nbsp;
            </div>
          </div>
        </AutoAffix>
        <div className="content-editor-input">
          <DraftEditor
            blockRendererFn={(block) => blockRenderer(block, this.handleContentStateDataChange, this.handleImageUrlToggle)}
            editorState={editorState}
            onChange={this.handleEditorChange}
            handleKeyCommand={this.handleKeyCommand}
            ref={(ref) => (this.refEditor = ref)}
            readOnly={disabled}
          />
        </div>

        <AssetSelectModal
          show={this.state.selecting}
          onSelect={this.handleSelection}
          onHide={this.handleSelectionToggle}
          onFileSelected={this.handleFileUpload}
        />
        {this.state.imageUrlEditor && <ImageBlockUrlModal
          show={this.state.imageUrlEditor}
          onHide={this.handleImageUrlToggle}
          setUrl={this.setImageUrl}
          url={this.state.imageInfo.url}
          urlInTab={this.state.imageInfo.urlInTab}
        />}
        {this.state.urlEditor && <ImageBlockUrlModal
          show={this.state.urlEditor}
          onHide={() => this.setState({urlEditor: false})}
          setUrl={this.setUrl}
          url={this.state.urlInfo.url}
          urlInTab={this.state.urlInfo.urlInTab}
        />}
      </div>
    );
  }
}

Editor.defaultProps = {
  onChange: () => {},
  blocks: [],
  disabled: false
};

export default Editor;
